import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

import { AbstractAuthService } from '../abstract_class/abstract-auth-service'
import { LoginResult, RequestModel, ResponseModel, ResultCode } from '../domain/authentication.model'
import { User } from '../domain/user'
import { CIDService } from './cid.service'
import { CoreEnvironmentService } from './core-environment.service'

@Injectable()
export class AuthService extends AbstractAuthService {
  private baseUrl: string
  private urls: any
  private controller = '/web/v1/auth/'

  user: BehaviorSubject<User> = new BehaviorSubject<User>(new User())
  resolveAuthorizationRequested: () => void
  authorizationRequested: Promise<any> = new Promise<any>((res: any, rej) => {
    this.resolveAuthorizationRequested = res
  })
  isAuthenticated = false
  avatar: string

  constructor(
    private cIdService: CIDService,
    private http: HttpClient,
    private coreEnvironmentService: CoreEnvironmentService
  ) {
    super()
    this.baseUrl = coreEnvironmentService.getBaseURL()
    this.urls = {
      login: this.baseUrl + this.controller + 'login',
      me: this.baseUrl + this.controller + 'me',
      logout: this.baseUrl + this.controller + 'logout',
      recoveryRequest: this.baseUrl + this.controller + 'request-recovery',
      recoveryConfirm: this.baseUrl + this.controller + 'confirm-recovery',
      updatePassword: this.baseUrl + this.controller + 'update-password',
      updateEmail: this.baseUrl + this.controller + 'update-email',
      confirmEmail: this.baseUrl + this.controller + 'confirm-email',
      registration: this.baseUrl + this.controller + 'registration'
    }
  }

  registration(email: string, name: string, password: string): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(this.urls.registration, { email, name, password })
  }

  login(email: string, password: string): Observable<ResponseModel<LoginResult>> {
    const cID = this.cIdService.getCID()
    const request = new RequestModel(email, password, cID)
    const result = this.http.post<ResponseModel<LoginResult>>(this.urls.login, request)

    result.subscribe(res => {
      if (res.resultCode === ResultCode.Success) {
        this.isAuthenticated = true
        this.me()
      }
    })
    return result
  }

  me(): Observable<ResponseModel<User>> {
    const result = this.http.get<ResponseModel<User>>(this.urls.me, { withCredentials: true })
    result.subscribe(res => {
      if (res.resultCode === ResultCode.Success) {
        this.isAuthenticated = true
        this.user.next(res.data)
      } else {
        this.isAuthenticated = false
      }
      this.resolveAuthorizationRequested()
    })
    return result
  }

  logout(): Observable<ResponseModel<LoginResult>> {
    const result = this.http.post<ResponseModel<LoginResult>>(this.urls.logout, null)
    result.subscribe(res => {
      if (res.resultCode === ResultCode.Success) {
        this.isAuthenticated = false
        this.user.value.isAuth = false
        this.user.value.isBeatmaker = null
        this.user.next(this.user.value)
      }
    })
    return result
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated
  }

  requestPasswordRecovery(email: string): Observable<ResponseModel<LoginResult>> {
    return this.http.post<ResponseModel<LoginResult>>(this.urls.recoveryRequest, { email })
  }

  changePassword(password: string, code: string): Observable<ResponseModel<LoginResult>> {
    return this.http.post<ResponseModel<LoginResult>>(this.urls.recoveryConfirm, { password, code })
  }

  changeEmailAddress(email: string, password: string): Observable<ResponseModel<LoginResult>> {
    return this.http.post<ResponseModel<LoginResult>>(
      this.urls.updateEmail,
      {
        email,
        password
      },
      { withCredentials: true }
    )
  }

  confirmNewEmail(code: string): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(this.urls.confirmEmail, { code })
  }

  setNewPassword(newPassword: string, currentPassword: string): Observable<ResponseModel<LoginResult>> {
    return this.http.post<ResponseModel<LoginResult>>(this.urls.updatePassword, { newPassword, currentPassword })
  }
}
